import Keycloak, { type KeycloakInitOptions } from 'keycloak-js';

type TokenChangeHandler = (token: string | null) => void;

export class KeycloakService {
  private static _instance: KeycloakService | null = null;

  private keycloak: Keycloak | null = null;
  private initPromise: Promise<Keycloak> | null = null;
  private tokenListeners: TokenChangeHandler[] = [];

  private constructor() { }

  public static getInstance(): KeycloakService {
    if (!KeycloakService._instance) {
      KeycloakService._instance = new KeycloakService();
    }
    return KeycloakService._instance;
  }

  public setRealm(realm: string): void {
    if (!realm) return;
    const prev = sessionStorage.getItem('realm');
    if (realm !== prev) {
      sessionStorage.setItem('realm', realm);
      this.reset();
    }
  }

  public getRealm(): string | null {
    return sessionStorage.getItem('realm');
  }

  public onTokenChange(cb: TokenChangeHandler): void {
    this.tokenListeners.push(cb);
  }

  public offTokenChange(cb: TokenChangeHandler): void {
    this.tokenListeners = this.tokenListeners.filter(fn => fn !== cb);
  }

  private notifyTokenChange() {
    const t = this.getToken();
    this.tokenListeners.forEach((cb) => cb(t));
  }

  public reset(): void {
    this.keycloak = null;
    this.initPromise = null;
  }

  public async init(): Promise<Keycloak> {
    if (this.initPromise) {
      return this.initPromise;
    }

    const realm = this.getRealm();
    if (!realm) {
      return Promise.reject(new Error('Keycloak realm non impostato'));
    }

    const url = import.meta.env.VITE_APP_KEYCLOAK_URL ?? '';
    const clientId = import.meta.env.VITE_APP_KEYCLOAK_CLIENT ?? 'filegrant';
    this.keycloak = new Keycloak({ url, realm, clientId });
    const opts: KeycloakInitOptions = {
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri:
        import.meta.env.VITE_APP_KEYCLOAK_ENABLE_SILENT_SSO === 'true'
          ? `${window.location.origin}/silent-check-sso.html`
          : undefined,
    };

    this.initPromise = new Promise<Keycloak>((resolve, reject) => {
      const kc = this.keycloak!;

      const cleanup = () => {
        kc.onReady = undefined!;
        kc.onAuthSuccess = undefined!;
        kc.onAuthError = undefined!;
        kc.onTokenExpired = undefined!;
      };

      kc.onReady = (authenticated) => {
        if (!authenticated) {
          cleanup();
          this.notifyTokenChange();
          return resolve(kc);
        }
      };

      kc.onAuthSuccess = () => {
        this.scheduleRefresh();
        cleanup();
        this.notifyTokenChange();
        resolve(kc);
      };

      kc.onAuthError = (err) => {
        cleanup();
        this.keycloak = null;
        this.initPromise = null;
        reject(err);
      };

      kc.onTokenExpired = () => {
        this.refreshToken().catch((e) => {
          console.error('Refresh expoirty failed:', e);
        });
      };

      kc.init(opts).catch((err) => {
        cleanup();
        this.keycloak = null;
        this.initPromise = null;
        reject(err);
      });
    });

    return this.initPromise;
  }

  public async refreshToken(minValiditySeconds = 60): Promise<boolean> {
    if (!this.keycloak) {
      throw new Error('Keycloak not available');
    }

    try {
      const did = await this.keycloak.updateToken(minValiditySeconds);
      this.notifyTokenChange();

      if (did) {
        this.scheduleRefresh();
      }

      return did;
    } catch (err) {
      console.error('Token refresh failed:', err instanceof Error ? err.message : 'Unknown error');

      await this.logout();
      throw err;
    }
  }

  private scheduleRefresh() {
    if (!this.keycloak?.tokenParsed?.exp) return;
    const exp = this.keycloak.tokenParsed.exp * 1000; // ms
    const now = Date.now();
    const ttl = exp - now;
    const refreshAt = ttl * 0.8; // 80%
    setTimeout(() => {
      this.refreshToken().catch((e) => console.error(e));
    }, refreshAt);
  }

  public async login(idpHint?: string) {
    await this.init();
    this.keycloak!.login({
      ...(idpHint ? { idpHint } : {}),
      redirectUri: window.location.origin,
    });
  }

  public async logout() {
    const idToken = this.keycloak?.idToken;

    this.keycloak?.logout({
      redirectUri: window.location.origin,
      ...(idToken ? { idTokenHint: idToken } : {}),
    });

    this.reset(); // cleanup local state
  }

  public getKeycloak(): Keycloak | null {
    if (
      import.meta.env.VITE_APP_KEYCLOAK_AUTH_ENABLED === 'true' &&
      this.getRealm() && this.keycloak
    ) {
      return this.keycloak;
    }
    return null;
  }

  public getToken(): string | null {
    return this.keycloak?.token ?? null;
  }

  public isAuthenticated(): boolean {
    return this.keycloak?.authenticated === true;
  }
}

export const keycloakService = KeycloakService.getInstance();
